import React, { useMemo } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Typography } from '@mui/material'
import {
  ASSIGNMENT_STATE_BLIND_PLUS,
  ASSIGNMENT_STATE_POST,
} from 'lib/assignmentStates'
import { orderBy } from 'lodash'
import { TABLE_DIRECTION_DESC, TABLE_WIDTH_DOT_CHOICE } from 'lib/tableLib'

import Table from 'components/atoms/Table'
import DotCell from 'components/atoms/DotCell'
import TableHeaderCell from 'components/atoms/TableHeaderCell'

import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useSort from 'hooks/useSort'

import {
  getAutoAssignmentDisplayState,
  getAssignmentState,
  getAverage,
  getDelta,
  handleDeltaErrors,
} from '../Assignment/assignmentLib'
import DeltaTableCell from '../Assignment/DeltaTableCell'

import AssignToBar from './AssignToBar'
import AssignToDialog from './AssignToDialog'

const isPresentValue = (value) =>
  value !== null && value !== undefined && value !== ''

const getPartnerBlindPrice = ({
  partner,
  assignmentCar,
  sourceCar,
  assignmentType,
  isBlindWinnerRow,
}) => {
  const explicitBlindPrice = [
    partner?.blindPrice,
    partner?.blindFinalPrice,
    partner?.blindOfferPrice,
    partner?.blind_price,
    partner?.blindOffer,
    partner?.blindValue,
    partner?.bidPrice,
    partner?.offerPrice,
    partner?.reprice,
  ].find((price) => isPresentValue(price))

  if (isPresentValue(explicitBlindPrice)) {
    return explicitBlindPrice
  }

  const assignedPartnerBlindResult = [
    assignmentCar?.blindFinalPrice,
    assignmentCar?.finalPrice,
    sourceCar?.blindFinalPrice,
    sourceCar?.finalPrice,
  ].find((price) => isPresentValue(price))

  if (isBlindWinnerRow && isPresentValue(assignedPartnerBlindResult)) {
    return assignedPartnerBlindResult
  }

  if (assignmentType === 'blind' || partner?.assignmentType === 'blind') {
    return null
  }

  return null
}

const hasPartnerBlindBid = ({
  partner,
  assignmentCar,
  sourceCar,
  assignmentType,
  isBlindWinnerRow,
}) => {
  const partnerMarkedBlind =
    partner?.assignmentType === 'blind' || partner?.secondChoice
  if (partnerMarkedBlind) {
    return true
  }

  if (isBlindWinnerRow) {
    return true
  }

  const carIsBlindAssignment = assignmentType === 'blind'
  if (!carIsBlindAssignment) {
    return false
  }

  const inBlindChoices =
    Array.isArray(
      assignmentCar?.firstChoiceList || sourceCar?.firstChoiceList
    ) &&
    partner?.partnerId &&
    (assignmentCar?.firstChoiceList || sourceCar?.firstChoiceList).includes(
      partner.partnerId
    )

  return (
    inBlindChoices ||
    (partner?.partnerId &&
      partner.partnerId ===
        (assignmentCar?.assignedPartnerId || sourceCar?.assignedPartnerId))
  )
}

const createColumns = (t, session, assignments, partners) => {
  let result = [
    {
      accessor: 'partnerName',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.partner')} />
      ),
    },
    {
      accessor: 'price',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.price')} />
      ),
    },
    {
      accessor: 'cost',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.cost')} />
      ),
    },
    {
      accessor: 'firstChoice',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.first')} />
      ),
      Cell: ({ row }) =>
        row.original.firstChoice && <DotCell color="primary" />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_DOT_CHOICE,
        },
      },
    },
    {
      accessor: 'secondChoice',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.second')} />
      ),
      Cell: ({ row }) =>
        row.original.secondChoice && <DotCell color="primary" />,
      cellProps: {
        sx: {
          width: TABLE_WIDTH_DOT_CHOICE,
        },
      },
    },
    {
      accessor: 'capDelta',
      Header: (
        <TableHeaderCell
          label={t('assignment.car-details.table.capDelta')}
          errorsNumber={handleDeltaErrors(
            assignments,
            partners,
            ASSIGNMENT_STATE_POST
          )}
        />
      ),
    },
    {
      accessor: 'avgIndex',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.avgIndex')} />
      ),
    },
    {
      accessor: 'status',
      Header: (
        <TableHeaderCell label={t('assignment.car-details.table.status')} />
      ),
      Cell: ({ value }) =>
        !!value && (
          <DotCell
            label={t(
              `assignment.status.${session.agenda.assignment.state}.${value}`
            )}
            color="primary"
          />
        ),
    },
  ]

  if (session.canReprice) {
    result.splice(
      result.findIndex((column) => column.accessor === 'capDelta'),
      0,
      {
        accessor: 'reprice',
        Header: (
          <TableHeaderCell label={t('assignment.car-details.table.reprice')} />
        ),
      }
    )
  }

  return result
}

export default function AssignmentCarTable({
  session,
  carChoice,
  handleAssignTo,
  isUpdating,
  readOnly,
}) {
  const { t } = useTranslation()

  const {
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useConfirmationDialog(handleAssignTo)

  const { order, setSort } = useSort('status', TABLE_DIRECTION_DESC)

  const car = useMemo(
    () =>
      carChoice.assignments.find((car) => car.plate === carChoice.car.plate),
    [carChoice]
  )

  const partners = useMemo(
    () => carChoice?.choices.filter((partner) => partner.groupId),
    [carChoice?.choices]
  )
  const sourceCar = carChoice?.car

  const partnersInTable = useMemo(
    () =>
      partners
        ?.map((partner) => {
          const isBlindWinnerRow =
            partner?.partnerId === car?.assignedPartnerId &&
            getAutoAssignmentDisplayState(car) === ASSIGNMENT_STATE_BLIND_PLUS
          const assignmentType =
            car?.assignmentType || sourceCar?.assignmentType
          const blindPrice = getPartnerBlindPrice({
            partner,
            assignmentCar: car,
            sourceCar,
            assignmentType,
            isBlindWinnerRow,
          })

          return ((!partner.firstChoice && !isPresentValue(blindPrice)) ||
            !partner.groupId) &&
            car?.assignedPartnerId !== partner.partnerId
            ? null
            : partner
        })
        .filter((partner) => partner),
    [car, sourceCar, partners]
  )

  const columns = useMemo(
    () => createColumns(t, session, carChoice.assignments, partnersInTable),
    [t, session, carChoice.assignments, partnersInTable]
  )

  const preparedItems = useMemo(() => {
    const numberFormatter = new Intl.NumberFormat('it-IT')

    return partnersInTable.map((partner) => {
      const assignmentType = car?.assignmentType || sourceCar?.assignmentType
      const isBlindWinnerRow =
        partner?.partnerId === car?.assignedPartnerId &&
        getAutoAssignmentDisplayState(car) === ASSIGNMENT_STATE_BLIND_PLUS
      const groupPrice = sourceCar?.[`price_group_${partner.groupId}`]
      const blindPrice = getPartnerBlindPrice({
        partner,
        assignmentCar: car,
        sourceCar,
        assignmentType,
        isBlindWinnerRow,
      })
      const hasBlindBid = hasPartnerBlindBid({
        partner,
        assignmentCar: car,
        sourceCar,
        assignmentType,
        isBlindWinnerRow,
      })
      const hasBlindPrice = isPresentValue(blindPrice)
      const price = hasBlindBid ? blindPrice : groupPrice

      const assignedCars = carChoice.assignments.filter(
        (car) => car.assignedPartnerId === partner.partnerId
      )

      const status =
        car?.assignedPartnerId === partner.partnerId
          ? getAssignmentState(car)
          : ''

      return {
        partnerName: partner.partnerName,
        price:
          hasBlindBid && !hasBlindPrice
            ? '-'
            : isPresentValue(price)
            ? numberFormatter.format(price)
            : '-',
        cost: numberFormatter.format(partner.cost),
        firstChoice: !!partner.firstChoice,
        secondChoice: hasBlindBid,
        reprice: (
          <Typography
            component="span"
            variant="body2"
            color={partner.reprice < car?.reservePrice ? 'error' : undefined}
          >
            {partner.reprice && numberFormatter.format(partner.reprice)}
          </Typography>
        ),
        capDelta: (
          <DeltaTableCell
            value={getDelta(assignedCars, partner, ASSIGNMENT_STATE_POST)}
          />
        ),
        avgIndex: getAverage(
          assignedCars
            .map((car) => car.appealing)
            .filter((appealing) => appealing)
        ),
        status,
        statusOrder: status ? Number.MAX_SAFE_INTEGER : partner.cost,
      }
    })
  }, [car, carChoice.assignments, sourceCar, partnersInTable])

  const sortedPartners = useMemo(
    () =>
      orderBy(
        preparedItems,
        [order?.column !== 'status' ? order?.column : 'statusOrder'],
        [order?.direction]
      ),
    [preparedItems, order?.column, order?.direction]
  )

  return (
    <>
      <Table
        items={sortedPartners || []}
        columns={columns}
        isFetching={isUpdating}
        sortable
        orderColumn={order?.column}
        orderDirection={order?.direction}
        handleSort={setSort}
        bottom={
          <AssignToBar
            errorNumber={
              session.canReprice
                ? partnersInTable.filter(
                    (partner) =>
                      partner.reprice !== null &&
                      partner.reprice < car?.reservePrice
                  ).length
                : undefined
            }
            handleAssignTo={handleOpenConfirmationDialog}
            readOnly={readOnly}
          />
        }
      />
      <AssignToDialog
        partners={partners}
        openConfirmationDialog={openConfirmationDialog}
        handleCloseConfirmationDialog={handleCloseConfirmationDialog}
        handleConfirmConfirmationDialog={handleConfirmConfirmationDialog}
      />
    </>
  )
}

AssignmentCarTable.propTypes = {
  session: T.object.isRequired,
  carChoice: T.object,
  handleAssignTo: T.func,
  isUpdating: T.bool,
  readOnly: T.bool,
}
